import {
  normaliseDomain,
  type NewsletterOptIn,
  type Theme,
  type ThemeOverrides,
} from '@quizee/core';
import type { LevelSettings } from '../api/types';

/**
 * Inheritance, in the words the settings screens use.
 *
 * The API resolves what a widget actually gets (see `resolveBrandConfig` in @quizee/core) and hands
 * back both halves: what a level has set, and what it resolves to. That is enough to render a
 * reader's view and not enough to render an editor's, because an editor has to know which of these
 * values they chose and where the rest came from. This module answers that second question, and it
 * is deliberately pure so the answer can be tested without a screen.
 *
 * Nothing here re-implements resolution. It walks the layers below the one being edited to name the
 * one that answered, and the value it reports is the value the API already resolved.
 */

/** What the bottom layer is called on screen. Not a publisher, so it is not named like one. */
export const SYSTEM_LABEL = 'the quizee default';

/** One layer under the level being edited, nearest first. */
export interface Layer {
  /** What to call it on screen: usually the publisher's name. */
  readonly label: string;
  readonly settings: LevelSettings;
}

/** Whether this level sets the field itself, rather than taking it from somewhere below. */
export function hasThemeOverride(theme: ThemeOverrides | null, field: keyof Theme): boolean {
  return theme !== null && theme[field] !== undefined;
}

export function withThemeField<K extends keyof Theme>(
  theme: ThemeOverrides | null,
  field: K,
  value: Theme[K],
): ThemeOverrides {
  return { ...theme, [field]: value };
}

/**
 * Stop overriding one field.
 *
 * The key is removed rather than set to undefined. Both read as "inherit" here, but only the
 * removal survives JSON, and this object is what gets sent.
 */
export function withoutThemeField(
  theme: ThemeOverrides | null,
  field: keyof Theme,
): ThemeOverrides {
  const { [field]: _cleared, ...rest } = theme ?? {};
  return rest;
}

/** True when nothing is overridden, which is sent as null rather than as an empty object. */
export function isEmptyTheme(theme: ThemeOverrides): boolean {
  return Object.values(theme).every((value) => value === undefined);
}

/** Every theme field, each one optional. What `ThemeOverrides` is, said in terms of `Theme`. */
type ThemeParts = { [K in keyof Theme]?: Theme[K] | undefined };

export interface Inherited<T> {
  readonly value: T;
  /** Where it comes from, named so a screen can say it in a sentence. */
  readonly from: string;
}

/**
 * What this field would be if the level being edited stopped setting it.
 *
 * The answer can be undefined, and only for the typeface: it is the one theme field with no system
 * default, because "whatever the widget brings with it" is a real answer rather than a colour we
 * could have picked.
 */
export function inheritedThemeField<K extends keyof Theme>(
  field: K,
  below: readonly Layer[],
  system: Theme,
): Inherited<Theme[K] | undefined> {
  for (const layer of below) {
    // Read through ThemeParts rather than ThemeOverrides: the two are the same six fields, but
    // one is inferred from a zod schema and through a generic key TypeScript cannot see that.
    const overrides: ThemeParts = layer.settings.theme ?? {};
    const value: Theme[K] | undefined = overrides[field];
    if (value !== undefined) return { value, from: layer.label };
  }
  return { value: system[field], from: SYSTEM_LABEL };
}

/**
 * The same for the consent notice, which is one value rather than six.
 *
 * An empty string counts as set. A brand that has chosen to show nothing has made a choice, and
 * treating it as an absence would quietly put the org's notice back on their articles.
 */
export function inheritedConsent(below: readonly Layer[], system: string): Inherited<string> {
  for (const layer of below) {
    const value = layer.settings.consent_text;
    if (value !== null) return { value, from: layer.label };
  }
  return { value: system, from: SYSTEM_LABEL };
}

/**
 * The same walk again for the newsletter wording, which is a separate statement from the notice.
 *
 * Separate rather than folded in with `inheritedConsent` because the two resolve per field: a
 * brand that writes its own newsletter sentence keeps the publisher's answer notice, and a brand
 * that writes its own answer notice keeps the publisher's newsletter sentence. An empty string
 * counts as set here too, and it means the brand has switched newsletter signups off rather than
 * gone back to inheriting.
 *
 * Undefined and null are both absences: undefined is a reply from before the field existed, null
 * is a level saying it sets nothing. Neither is a value, so both walk on.
 */
export function inheritedNewsletterConsent(
  below: readonly Layer[],
  system: string,
): Inherited<string> {
  for (const layer of below) {
    const value = layer.settings.newsletter_consent_text;
    if (value !== undefined && value !== null) return { value, from: layer.label };
  }
  return { value: system, from: SYSTEM_LABEL };
}

/**
 * The same again for opt in mode, which is one value with two answers.
 *
 * Null and undefined both mean "nothing chosen here": a brand sends null to stop overriding, and an
 * older reply carries no field at all. Neither is a value, so both walk on to the next layer, and
 * a publisher who has set single opt in is what a brand goes back to rather than the quizee
 * default.
 */
export function inheritedOptIn(
  below: readonly Layer[],
  system: NewsletterOptIn,
): Inherited<NewsletterOptIn> {
  for (const layer of below) {
    const value = layer.settings.newsletter_opt_in;
    if (value !== undefined && value !== null) return { value, from: layer.label };
  }
  return { value: system, from: SYSTEM_LABEL };
}

/**
 * The three things a level can say about the consent notice.
 *
 * They are three because two of them look alike and are not: 'inherit' sends null and takes
 * whatever the layer below says, 'none' sends an empty string and shows readers nothing. One
 * control that cleared to empty would make those the same gesture, and a publisher who meant to go
 * back to their default notice would silently be showing no notice at all.
 */
export type ConsentChoice = 'inherit' | 'own' | 'none';

export function consentChoiceOf(value: string | null): ConsentChoice {
  if (value === null) return 'inherit';
  return value === '' ? 'none' : 'own';
}

/** What to send for a choice. Null clears the override, a string sets one. */
export function consentPatchValue(choice: ConsentChoice, text: string): string | null {
  if (choice === 'inherit') return null;
  return choice === 'none' ? '' : text;
}

/**
 * What the server stored, when sanitising changed what was sent, and null when there is nothing
 * to report.
 *
 * Only a wording somebody actually wrote can come back different. Going back to inherited sends
 * null and showing nothing sends an empty string, and neither has a tidied form worth a sentence.
 */
export function tidiedDifference(
  sent: string | null | undefined,
  stored: string | null | undefined,
): string | null {
  if (sent === undefined || sent === null || sent === '') return null;
  const held = stored ?? '';
  return held === sent ? null : held;
}

/** One theme field, as it is put to an editor. */
export interface ThemeFieldSpec {
  readonly field: keyof Theme;
  readonly label: string;
  readonly description: string;
}

export const THEME_FIELDS: readonly ThemeFieldSpec[] = [
  {
    field: 'accent_colour',
    label: 'Accent colour',
    description: 'Buttons, the bar on a result, and anything else that has to stand out.',
  },
  {
    field: 'text_colour',
    label: 'Text colour',
    description: 'The words readers read, and the lines around the options.',
  },
  {
    field: 'background_colour',
    label: 'Background colour',
    description: 'Behind the whole interaction. White sits quietly inside most articles.',
  },
  {
    field: 'corner_radius',
    label: 'Corner rounding',
    description: 'In pixels. 0 is square corners, 40 is as round as it goes.',
  },
  {
    field: 'font_family',
    label: 'Typeface',
    description:
      'The name of a font your own site already loads, such as Georgia. Left empty, readers get the typeface the widget brings with it.',
  },
  {
    field: 'show_brand_credit',
    label: 'quizee credit',
    description: 'A small "Powered by quizee" line under the interaction.',
  },
];

export type DomainVerdict =
  | { readonly ok: true; readonly domain: string; readonly tidied: boolean }
  | { readonly ok: false; readonly reason: string };

/**
 * Read one domain the way the server will, and say why in words when it will not do.
 *
 * `normaliseDomain` is the server's own function, imported rather than copied, so this screen
 * cannot accept something the API would then refuse. It answers null for every kind of failure
 * though, and "invalid domain" is not a sentence an editor can act on. So a refusal is put to it a
 * second time with a label glued on the front: `co.uk` fails and `example.co.uk` passes, which
 * means the entry was a suffix everybody shares rather than nonsense. Doing it that way keeps the
 * list of shared suffixes in one place, in @quizee/core, instead of growing a second copy here
 * that would drift out of step with the one that decides.
 */
export function readDomain(entry: string): DomainVerdict {
  const raw = entry.trim();
  if (raw === '') {
    return { ok: false, reason: 'Type the website this brand publishes on, such as herald.co.uk.' };
  }

  const domain = normaliseDomain(raw);
  if (domain !== null) return { ok: true, domain, tidied: domain !== raw };

  const looksLikeHost = /^[a-z0-9.-]+$/i.test(raw);
  if (looksLikeHost && normaliseDomain(`example.${raw}`) !== null) {
    return {
      ok: false,
      reason: `Anybody can have a website under ${raw.toLowerCase()}, so it is not yours to allow: every one of them could then post responses into your results. Use the domain your own articles are on, such as herald.co.uk.`,
    };
  }

  return {
    ok: false,
    reason: `We could not read ${raw} as a website address. Use the domain your articles are on, such as herald.co.uk, with nothing after it.`,
  };
}

/**
 * The entry a 400 invalid_domain was about, so the message can sit against the box that caused it.
 *
 * The API's message starts with the entry it could not read. Matching on that rather than on a
 * position keeps working when the server tidies the list, and anything it cannot match falls back
 * to the whole form, which is where an unattributed error belongs.
 */
export function domainAtFault(message: string, entries: readonly string[]): number {
  return entries.findIndex((entry) => {
    const raw = entry.trim();
    return raw !== '' && message.startsWith(raw);
  });
}

const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;

/**
 * What is wrong with the theme somebody has typed, in sentences rather than field names.
 *
 * The API validates all of this again and would refuse the save, but its message is written for a
 * developer reading a log. These are written for the person looking at the box.
 */
export function themeProblems(theme: ThemeOverrides): readonly string[] {
  const problems: string[] = [];

  for (const spec of THEME_FIELDS) {
    const value = theme[spec.field];
    if (value === undefined) continue;

    if (spec.field.endsWith('_colour') && (typeof value !== 'string' || !HEX_COLOUR.test(value))) {
      problems.push(`${spec.label} needs to be a colour code of six characters, such as #1f5fd8.`);
    }
    if (spec.field === 'corner_radius') {
      const rounded = typeof value === 'number' ? value : Number.NaN;
      if (!Number.isInteger(rounded) || rounded < 0 || rounded > 40) {
        problems.push('Corner rounding needs to be a whole number of pixels between 0 and 40.');
      }
    }
    if (spec.field === 'font_family' && typeof value === 'string' && value.length > 200) {
      problems.push('The typeface name is too long. 200 characters is the most it can be.');
    }
  }

  return problems;
}
